<script setup lang="ts">
import type { PaAPI } from '@rialtic/types'
import { useWorkspace } from './stores/workspace'

type StateCode = PaAPI.Schemas['StateCode']

const currentUser = useCurrentUser()
const { $auth0, $groundcover, $pendo } = useNuxtApp()
const { user } = $auth0()
const route = useRoute()
const workspace = useWorkspace()
const { WORKERS_API_BASE_URL } = useRuntimeConfig().public

useHead(() => ({
  title: (route.meta.title as string) || undefined,
  titleTemplate: (s) => {
    return s ? `${s} | Rialtic Console` : 'Rialtic Console'
  },
  link: [{ rel: 'preconnect', href: WORKERS_API_BASE_URL }],
}))

watch(user, async (auth0User) => {
  if (!auth0User) {
    workspace.resetSession()
    currentUser.value = null
    return
  }

  const fetchedUser = await workspace.getUser()

  // A newer auth0 user has already superseded this one — discard this
  // stale result instead of overwriting the current session's data.
  if (user.value?.sub !== auth0User.sub) {
    return
  }

  currentUser.value = fetchedUser
})

watch(
  [currentUser, () => workspace.workspaceId],

  ([currUser]) => {
    if (!currUser) {
      return
    }
    const { id, email, workspaceRoles } = currUser

    const name = user.value
      ? `${user.value.given_name} ${user.value.family_name}`
      : `${currUser?.first_name} ${currUser?.last_name}`

    const roles =
      user.value?.['https://auth.rialtic.io/roles'] ||
      Object.keys(workspaceRoles).filter((key) =>
        key.startsWith(workspace.workspaceId),
      )

    $groundcover?.identifyUser({
      id,
      name,
      email,
      properties: {
        roles: roles.join(', '),
        email_domain: email.split('@')[1],
      },
    })
    ;($pendo.identify as any)({
      visitor: {
        id: email,
        email,
        full_name: name,
        role: roles[0] ?? '',
        roles: roles.join(', '),
      },
      account: {
        id: email.split('@')[1] || workspace.workspaceId,
        name: workspace.activeWorkspace?.name,
        rialtic_app: 'console',
        workspace_id: workspace.workspaceId,
      },
    })
  },
  {
    immediate: true,
  },
)

const getRefData = async (
  code: string,
  model: string,
  state_code?: StateCode,
) => {
  const { isAuthenticated, getAccessTokenSilently } = $auth0()
  const token = isAuthenticated.value
    ? await getAccessTokenSilently()
    : undefined
  const api = useRpcClient(token)
  const res = await api['ref-data'][':model'].$get({
    param: {
      model,
    },
    query: {
      search_phrase: model === 'Diagnosis' ? code.replace('.', '') : code,
      state_code,
    },
  })

  const data = await res.json()
  if (data?.length) {
    return data[0]
  }
  return null
}

const userPermissions = computed(() => currentUser.value?.permissions)
provide('user-permissions', userPermissions)
provide('getRefData', getRefData)
</script>

<template>
  <div class="font-poppins bg-surface-bg">
    <VitePwaManifest />
    <NuxtLoadingIndicator
      color="repeating-linear-gradient(to right,#6F7CCD 0%,#28B7F0 62%,#35CDDA 100%)"
    />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>
